// david.js — Phase 3.7 + 3.8: David Sidebar
// Action queue scaffold + full Claude API integration

import { apiFetch } from './utils.js';
import { state } from './state.js';

let pollInterval = null;
let wsInitialized = false;
let loadInFlight = false;
let loadDebounceTimer = null;

export function initDavidSidebar() {
  // Mount sidebar into app-main layout
  const layout = document.querySelector('.app-main');
  if (!layout) return;

  if (!document.getElementById('david-sidebar')) {
    const sidebar = document.createElement('div');
    sidebar.id = 'david-sidebar';
    sidebar.className = 'david-sidebar';
    sidebar.innerHTML = renderSidebarShell();
    layout.appendChild(sidebar);
  }

  loadSidebarData();
  initWebSocketConnection();
}

function renderSidebarShell() {
  return `
    <div class="david-header">
      <div class="david-title">
        <span class="david-name">✦ David</span>
        <span class="david-live-dot" id="david-live-dot"></span>
      </div>
      <span class="david-subtitle">AI Advisor</span>
    </div>

    <div class="david-section alerts-section">
      <div class="david-section-label">Alerts</div>
      <div id="david-alerts" class="david-alerts-list">
        <div class="david-loading">Loading…</div>
      </div>
    </div>

    <div class="david-section queue-section">
      <div class="david-section-label">
        Action Queue
        <span class="queue-count" id="queue-count">0</span>
      </div>
      <div id="david-queue" class="david-queue-list">
        <div class="david-loading">Loading…</div>
      </div>
    </div>

    <div class="david-section chat-section">
      <div class="david-section-label">Chat</div>
      <div id="david-chat-history" class="david-chat-history"></div>
      <div class="david-chat-input-row">
        <textarea id="david-chat-input" class="david-chat-input"
                  placeholder="Ask David…"
                  onkeydown="if(event.key==='Enter'&&!event.shiftKey){event.preventDefault();window.sendDavidMessage();}"
                  rows="2"></textarea>
        <button onclick="window.sendDavidMessage()" class="david-send-btn">→</button>
      </div>
    </div>
  `;
}

async function loadSidebarData() {
  if (loadInFlight) return; // prevent concurrent fetches from racing the DOM
  loadInFlight = true;
  try {
    const actions = await apiFetch('/api/advisor/pending-actions');

    const alerts = actions.filter(a => a.action_type === 'alert');
    const queue  = actions.filter(a => a.action_type !== 'alert');

    renderAlerts(alerts);
    renderQueue(queue);

    const liveDot = document.getElementById('david-live-dot');
    if (liveDot) {
      const recent = actions.some(a => {
        const age = Date.now() - new Date(a.created_at).getTime();
        return age < 3600000;
      });
      liveDot.classList.toggle('active', recent);
    }
  } catch (err) {
    console.error('[David] Failed to load sidebar data:', err.message);
  } finally {
    loadInFlight = false;
  }
}

function scheduleSidebarLoad() {
  if (loadDebounceTimer) clearTimeout(loadDebounceTimer);
  loadDebounceTimer = setTimeout(loadSidebarData, 300);
}

function renderAlerts(alerts) {
  const el = document.getElementById('david-alerts');
  if (!el) return;

  if (alerts.length === 0) {
    el.innerHTML = '<div class="david-empty">No alerts</div>';
    return;
  }

  el.innerHTML = alerts.slice(0, 3).map(alert => `
    <div class="david-alert priority-${alert.priority || 'normal'}" data-id="${alert.id}">
      <div class="alert-title">${alert.title}</div>
      ${alert.body ? `<div class="alert-body">${alert.body}</div>` : ''}
      ${alert.client_name ? `<div class="alert-client">${alert.client_name}</div>` : ''}
      <button class="alert-dismiss" onclick="window.dismissAlert(${alert.id})">×</button>
    </div>
  `).join('');
}

function renderQueue(queueItems) {
  const el = document.getElementById('david-queue');
  const countEl = document.getElementById('queue-count');
  if (!el) return;

  if (countEl) countEl.textContent = queueItems.length;

  if (queueItems.length === 0) {
    el.innerHTML = '<div class="david-empty">Queue is clear</div>';
    return;
  }

  el.innerHTML = queueItems.map(item => `
    <div class="queue-item type-${item.action_type}" data-id="${item.id}">
      <div class="queue-item-type">${formatActionType(item.action_type)}</div>
      <div class="queue-item-title">${item.title}</div>
      ${item.client_name ? `<div class="queue-item-client">${item.client_name}</div>` : ''}
      ${item.body ? `<div class="queue-item-preview">${item.body.substring(0, 100)}${item.body.length > 100 ? '…' : ''}</div>` : ''}
      <div class="queue-item-actions">
        <button class="queue-btn accept" onclick="window.acceptAction(${item.id})">Accept</button>
        <button class="queue-btn edit"   onclick="window.editAction(${item.id})">Edit</button>
        <button class="queue-btn reject" onclick="window.rejectAction(${item.id})">✗</button>
      </div>
    </div>
  `).join('');
}

function formatActionType(type) {
  const labels = {
    staged_email:  'Staged Email',
    note:          'Note',
    field_update:  'Field Update',
    alert:         'Alert',
  };
  return labels[type] || type;
}

// ── WebSocket — real-time queue updates ───────────────────────────────────

function initWebSocketConnection() {
  if (wsInitialized) return; // only one WS connection per page load
  wsInitialized = true;

  try {
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${location.host}?session=${state.davidSessionId || 'crm-default'}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      // WebSocket active — clear any polling fallback
      if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'new_action') {
          // Debounce: collapse rapid broadcasts into a single reload
          scheduleSidebarLoad();
          const badge = document.getElementById('queue-count');
          if (badge) {
            badge.classList.add('flash');
            setTimeout(() => badge.classList.remove('flash'), 1000);
          }
        }
        if (msg.type === 'pending_count') {
          const badge = document.getElementById('queue-count');
          if (badge) badge.textContent = msg.count;
        }
      } catch {}
    };

    ws.onclose = () => {
      wsInitialized = false; // allow reconnect on next initDavidSidebar call
      // WebSocket dropped — fall back to polling
      if (!pollInterval) pollInterval = setInterval(loadSidebarData, 30000);
    };

    ws.onerror = () => {
      if (!pollInterval) pollInterval = setInterval(loadSidebarData, 30000);
    };
  } catch (err) {
    // WebSocket unavailable (e.g. old browser) — fall back to polling
    wsInitialized = false;
    if (!pollInterval) pollInterval = setInterval(loadSidebarData, 30000);
  }
}

// ── Action handlers ────────────────────────────────────────────────────────

window.acceptAction = async function(id) {
  try {
    await apiFetch(`/api/advisor/action/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'accepted' })
    });
    await loadSidebarData();
  } catch (err) {
    console.error('[David] Accept failed:', err.message);
  }
};

window.rejectAction = async function(id) {
  try {
    await apiFetch(`/api/advisor/action/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'rejected' })
    });
    await loadSidebarData();
  } catch (err) {
    console.error('[David] Reject failed:', err.message);
  }
};

window.dismissAlert = async function(id) {
  await window.rejectAction(id);
};

window.editAction = function(id) {
  const item = document.querySelector(`.queue-item[data-id="${id}"]`);
  if (!item) return;

  const previewEl = item.querySelector('.queue-item-preview');
  if (!previewEl) return;

  const currentText = previewEl.textContent.replace(/…$/, '');
  previewEl.innerHTML = `<textarea class="queue-edit-area">${currentText}</textarea>`;

  const actionsEl = item.querySelector('.queue-item-actions');
  actionsEl.innerHTML = `
    <button class="queue-btn accept" onclick="window.saveActionEdit(${id}, this)">Save</button>
    <button class="queue-btn reject" onclick="window._davidReload()">Cancel</button>
  `;
};

window.saveActionEdit = async function(id, btn) {
  const item = btn.closest('.queue-item');
  const textarea = item?.querySelector('.queue-edit-area');
  const newBody = textarea?.value;

  try {
    await apiFetch(`/api/advisor/action/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'edited', body: newBody })
    });
    await loadSidebarData();
  } catch (err) {
    console.error('[David] Save edit failed:', err.message);
  }
};

// Exposed for inline onclick handlers in rendered HTML
window._davidReload = loadSidebarData;

// ── Chat — Phase 3.8: full Claude integration ──────────────────────────────

window.sendDavidMessage = async function() {
  const input = document.getElementById('david-chat-input');
  const message = input?.value.trim();
  if (!message) return;

  appendChatMessage('user', message);
  input.value = '';
  const thinkingEl = appendChatMessage('david', '…', 'thinking');

  try {
    const result = await apiFetch('/api/david/chat', {
      method: 'POST',
      body: JSON.stringify({
        message,
        session_id: state.davidSessionId || generateSessionId(),
        context: {
          view:      state.currentView,
          client_id: state.currentClientId,
          show_id:   state.currentShowId,
        }
      })
    });

    if (!state.davidSessionId) state.davidSessionId = result.session_id;

    thinkingEl?.remove();
    appendChatMessage('david', result.reply);
  } catch (err) {
    thinkingEl?.remove();
    appendChatMessage('david', 'Sorry, I had trouble with that. Try again.');
    console.error('[David] Chat failed:', err.message);
  }
};

function appendChatMessage(role, text, extraClass) {
  const history = document.getElementById('david-chat-history');
  if (!history) return null;

  const div = document.createElement('div');
  div.className = `chat-msg ${role}${extraClass ? ' ' + extraClass : ''}`;
  div.textContent = text;
  history.appendChild(div);
  history.scrollTop = history.scrollHeight;
  return div;
}

function generateSessionId() {
  const id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  state.davidSessionId = id;
  return id;
}
